import { Flex, Fieldset } from '@chakra-ui/react';
import { Controller, Path, PathValue, useFormContext, useWatch } from 'react-hook-form';
import { Checkbox } from '@/components/ui/checkbox';
import { useEffect } from 'react';
import { disciplinesFlattened as disciplines } from '@/utils/coding-details';
import { FieldProps } from './clinical-items/types';

interface SoftwareFeaturesProps<T> {
    hh: FieldProps<T>;
    hhServices: FieldProps<T>;
    hospice: FieldProps<T>;
    pca: FieldProps<T>;
    error: boolean;
    errorMessage?: string;
}

export function SoftwareFeatures<T extends object>({ hh, hhServices, hospice, pca, error, errorMessage }: SoftwareFeaturesProps<T>) {
    const { control, setValue } = useFormContext<T>();

    const homeHealthServices = useWatch({ control: control, name: hhServices.path });
    const homeHealth = useWatch({ control: control, name: hh.path });

    useEffect(() => {
        const values = homeHealthServices ? Object.values(homeHealthServices) : [];
        const someChecked = values.some(Boolean);

        // sync state of "nested" checkboxes with parent.
        if (homeHealth !== someChecked) {
            setValue(hh.path, someChecked as PathValue<T, Path<T>>);
        }
    }, [hh.path, homeHealthServices, homeHealth, setValue]);

    return (
        <Fieldset.Root invalid={!!error}>
            <Fieldset.Content>
                <Controller
                    control={control}
                    name={hh.path}
                    render={({ field: { value, onChange, ...props } }) => {
                        const values = homeHealthServices ? Object.values(homeHealthServices) : [];
                        const someChecked = values.some(Boolean);
                        const allChecked = values.length > 0 && values.every(Boolean);
                        const isIndeterminate = someChecked && !allChecked;

                        return (
                            <Checkbox
                                {...props}
                                checked={isIndeterminate ? 'indeterminate' : allChecked}
                                onCheckedChange={(e) => {
                                    onChange(e.checked);
                                    disciplines.forEach((discipline) => {
                                        setValue(
                                            `${hhServices.path}.${discipline}` as Path<T>,
                                            Boolean(e.checked) as PathValue<T, Path<T>>,
                                        );
                                    });
                                }}>
                                Home Health
                            </Checkbox>
                        );
                    }}
                />

                <Flex pl="8" gap="3" flexDirection="column">
                    {disciplines.map((discipline) => (
                        <Controller
                            key={discipline}
                            control={control}
                            name={`${hhServices.path}.${discipline}` as Path<T>}
                            render={({ field: { value, onChange, ...props } }) => (
                                <Checkbox
                                    {...props}
                                    checked={value}
                                    onCheckedChange={(e) => {
                                        onChange(e.checked);
                                    }}>
                                    {discipline}
                                </Checkbox>
                            )}
                        />
                    ))}
                </Flex>
                <Controller
                    control={control}
                    name={hospice.path}
                    render={({ field: { value, onChange, ...props } }) => (
                        <Checkbox
                            {...props}
                            checked={value}
                            onCheckedChange={(e) => {
                                onChange(e.checked);
                            }}>
                            Hospice
                        </Checkbox>
                    )}
                />
                <Controller
                    control={control}
                    name={pca.path}
                    render={({ field: { value, onChange, ...props } }) => (
                        <Checkbox
                            {...props}
                            checked={value}
                            onCheckedChange={(e) => {
                                onChange(e.checked);
                            }}>
                            PCA
                        </Checkbox>
                    )}
                />
            </Fieldset.Content>
            <Fieldset.ErrorText>{errorMessage}</Fieldset.ErrorText>
        </Fieldset.Root>
    );
}
